// hooks/useBundleStatus.ts
/**
 * Hook for calculating bundle status for participants
 * Provides memoized bundle calculations based on participant data
 */

import { useMemo, useCallback, useRef } from 'react';
import { CombinedApiData, getParticipantAnchorValue } from '../interfaces/participantTrackerTypes';
import {
  BundleStatusInfo,
  calculateBundleStatus,
  parseVVPResultsToBundles,
  rebucketHomeTestsToVisitWindows,
  transformApiDataToBundles,
} from '../utils/bundleUtils';
import { ProtocolConfig, getBundleScheduleAnchorDate } from '../types/protocolConfig';
import { useProtocolConfigContext } from '../ProtocolConfigContext';
import { parseApiDate } from '../utils/dateUtils';

// ============================================================
// TYPES
// ============================================================

export interface UseBundleStatusOptions {
  /** Protocol configuration override (defaults to the server-driven context config) */
  config?: ProtocolConfig;
  /** Reference date for calculations (defaults to now) */
  referenceDate?: Date;
}

export interface UseBundleStatusResult {
  /** Calculate bundle status for a single participant (null until config loads) */
  getBundleStatus: (participant: CombinedApiData) => BundleStatusInfo | null;
  /** Calculate bundle status for multiple participants (empty until config loads) */
  getBundleStatusBatch: (participants: CombinedApiData[]) => Map<string, BundleStatusInfo>;
  /** Protocol configuration being used; null until the server ruleset loads */
  config: ProtocolConfig | null;
}

// ============================================================
// HOOK IMPLEMENTATION
// ============================================================

/**
 * Hook for calculating bundle status
 *
 * @param options - Configuration options
 * @returns Bundle status calculation functions
 *
 * @example
 * ```tsx
 * const { getBundleStatus } = useBundleStatus();
 * const status = getBundleStatus(participantData);
 * ```
 */
// Stable reference date that doesn't change between renders
const getStableReferenceDate = () => new Date();

export const useBundleStatus = (options: UseBundleStatusOptions = {}): UseBundleStatusResult => {
  const { config: contextConfig } = useProtocolConfigContext();
  const config = options.config ?? contextConfig;

  // Use a stable reference date - only set once on mount
  const referenceDateRef = useRef<Date>(options.referenceDate || getStableReferenceDate());
  const referenceDate = referenceDateRef.current;

  /**
   * Calculate bundle status for a single participant.
   * Returns null until the protocol config has loaded — guards against
   * computing statuses before the server ruleset is available.
   */
  const getBundleStatus = useCallback((participant: CombinedApiData): BundleStatusInfo | null => {
    if (!config) return null;
    const randDate = parseApiDate(getParticipantAnchorValue(participant));
    const actualSv3 = parseApiDate(participant.actual_sv3_date);
    const baselineDate = getBundleScheduleAnchorDate(config, randDate, actualSv3);

    const shouldRebucket = Boolean(
      baselineDate && (
        config.bundles.scheduleAnchor === 'actual_baseline' ||
        config.bundles.defaultWindowDaysBefore !== undefined ||
        config.bundles.defaultWindowDaysAfter !== undefined
      )
    );

    const rebucketed = shouldRebucket
      ? rebucketHomeTestsToVisitWindows(
          config,
          participant.vvp_results,
          baselineDate,
          participant.home_test_unix_times || null
        )
      : null;

    const serverBundles = rebucketed
      ? rebucketed.bundles
      : participant.vvp_results
      ? parseVVPResultsToBundles(participant.vvp_results)
      : transformApiDataToBundles(participant.home_test_unix_times || []);

    return calculateBundleStatus(
      config,
      serverBundles,
      baselineDate,
      referenceDate,
      rebucketed?.allActivityTimestamps ?? participant.home_test_unix_times ?? null
    );
  }, [config, referenceDate]);

  /**
   * Calculate bundle status for multiple participants
   * Returns a Map keyed by patient_id for efficient lookup
   */
  const getBundleStatusBatch = useCallback(
    (participants: CombinedApiData[]): Map<string, BundleStatusInfo> => {
      const statusMap = new Map<string, BundleStatusInfo>();

      participants.forEach(participant => {
        if (participant.patient_id) {
          const status = getBundleStatus(participant);
          if (status) {
            statusMap.set(participant.patient_id, status);
          }
        }
      });

      return statusMap;
    },
    [getBundleStatus]
  );

  return {
    getBundleStatus,
    getBundleStatusBatch,
    config,
  };
};

// ============================================================
// MEMOIZED HOOK FOR TABLE DATA
// ============================================================

/**
 * Hook that provides memoized bundle status for all participants in table data
 * Useful when you want to calculate once and access many times
 *
 * @param participants - Array of participant data
 * @param options - Configuration options
 * @returns Map of patient_id to BundleStatusInfo
 *
 * @example
 * ```tsx
 * const bundleStatusMap = useBundleStatusMap(tableData);
 * const status = bundleStatusMap.get(participantId);
 * ```
 */
export const useBundleStatusMap = (
  participants: CombinedApiData[],
  options: UseBundleStatusOptions = {}
): Map<string, BundleStatusInfo> => {
  const { getBundleStatusBatch } = useBundleStatus(options);

  return useMemo(
    () => getBundleStatusBatch(participants),
    [participants, getBundleStatusBatch]
  );
};

// ============================================================
// INDIVIDUAL PARTICIPANT HOOK
// ============================================================

/**
 * Hook for getting bundle status of a single participant
 * Memoizes the result based on participant data
 *
 * @param participant - Single participant data
 * @param options - Configuration options
 * @returns BundleStatusInfo for the participant
 *
 * @example
 * ```tsx
 * const bundleStatus = useParticipantBundleStatus(selectedParticipant);
 * ```
 */
export const useParticipantBundleStatus = (
  participant: CombinedApiData | null | undefined,
  options: UseBundleStatusOptions = {}
): BundleStatusInfo | null => {
  const { getBundleStatus } = useBundleStatus(options);

  return useMemo(() => {
    if (!participant) return null;
    return getBundleStatus(participant);
  }, [participant, getBundleStatus]);
};

// ============================================================
// EXPORTS
// ============================================================

export default useBundleStatus;
